// src/pages/EventDetails.jsx (NEW and FINAL for React)

import React, { useState } from "react"; 
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import moment from "moment";
import axiosInstance from "../context/axiosInstance";
import InviteGuestsModal from "./InviteGuestsModal";
import EditEventModal from "./EditEventModal";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
  FileText,
  MoreVertical,
  Pencil,
  Trash2,
  Settings,
  UserPlus,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import AppFooter from "./Homepage/AppFooter"; // Assuming this exists

// --- Reusable UI Sub-Components ---

const DetailRow = ({ icon, text }) => (
  <div className="flex items-center gap-4 py-2">
    <div className="flex-shrink-0">{icon}</div>
    <span className="text-gray-200">{text}</span>
  </div>
);

const InviteeStatusPill = ({ status }) => {
  let style = "";
  switch (status?.toLowerCase()) {
    case "accepted":
    case "responded":
      style = "bg-green-500/20 text-green-300";
      break;
    case "declined":
      style = "bg-red-500/20 text-red-300";
      break;
    default:
      style = "bg-yellow-500/20 text-yellow-300";
  }
  return (
    <span
      className={`text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full ${style}`}
    >
      {status}
    </span>
  );
};

const VenueRow = ({ venue }) => {
  if (!venue) {
    return (
      <DetailRow
        icon={<MapPin size={20} className="text-blue-400" />}
        text="Location not specified"
      />
    );
  }

  // Handle legacy string-based venues for backward compatibility
  if (typeof venue === "string") {
    const mapSearchUrl = `https://www.google.com/maps?q=${encodeURIComponent(
      venue
    )}`;
    return (
      <div className="flex items-start gap-4 py-2">
        <MapPin size={20} className="text-blue-400 mt-1" />
        <div className="flex-1">
          <p className="text-gray-200 leading-snug">{venue}</p>
          <a
            href={mapSearchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-1 text-blue-400 text-sm hover:underline"
          >
            Search on Google Maps <ExternalLink size={14} />
          </a>
        </div>
      </div>
    );
  }

  // ✅ THE FIX: Use `lat` and `lng` to match your data object
  const { address, lat, lng } = venue;

  // Now, check for the corrected variable names
  const mapUrl =
    lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : null;

  return (
    <div className="flex items-start gap-4 py-2">
      <MapPin size={20} className="text-blue-400 mt-1" />
      <div className="flex-1">
        <p className="text-gray-200 leading-snug">
          {address || "Location specified on map"}
        </p>

        {mapUrl && (
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-1 text-blue-400 text-sm hover:underline"
          >
            Open in Google Maps <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );
};

const EventDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams(); // Get eventId from URL like /app/events/:eventId
const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
const [isEditModalOpen, setIsEditModalOpen] = useState(false);
const [event, setEvent] = useState(location.state?.event);

const refetchEvent = async () => {
  try {
    const { data } = await axiosInstance.get(`/events/${eventId}`);
    setEvent(data.event);
  } catch (err) {
    console.error("Refresh error:", err);
    // Silent fail or minimal toast is fine
  }
};



  // If no event data is passed, show an error or redirect
  if (!event) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <p>Event data not found. Please go back to your appointments.</p>
        <button
          onClick={() => navigate("/app/appointments")}
          className="ml-4 text-blue-400"
        >
          Go Back
        </button>
      </div>
    );
  }

  // --- Data Extraction ---
  const {
    eventName,
    user_roles = [],
    description,
    venue,
    inviteeRecords = [],
    eventDateTime,
    rsvpQuestionnaire = [],
  } = event;

  const isHost = user_roles.includes("host");
  const isCoHost = user_roles.includes("co-host");

  const questionMap = rsvpQuestionnaire.reduce((acc, q) => {
    acc[q.questionId] = q.questionText;
    return acc;
  }, {});

  // --- Handlers ---
  const handleCancelEvent = async () => {
    toast.promise(axiosInstance.post(`/events/${eventId}/cancel`), {
      loading: "Cancelling event...",
      success: (res) => {
        navigate("/app/appointments", { replace: true });
        return res.data.message || "Event cancelled successfully.";
      },
      error: (err) => err.response?.data?.error || "Failed to cancel event.",
    });
  };

  const confirmCancel = () => {
    toast(
      (t) => (
        <div className="flex flex-col gap-4">
          <p className="font-bold">
            Are you sure you want to cancel this event?
          </p>
          <p className="text-sm text-gray-400">This action cannot be undone.</p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                handleCancelEvent();
              }}
              className="flex-1 bg-red-600 text-white font-bold py-2 rounded-lg"
            >
              Yes, Cancel
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="flex-1 bg-gray-700 text-white font-bold py-2 rounded-lg"
            >
              No
            </button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  // --- Render Sticky Action Button ---
  const renderStickyActionButton = () => {
    if (!isHost && !isCoHost) return null;

    const buttonProps = isHost
      ? {
          text: "Edit Event",
          icon: <Settings size={18} />,
          action: () => setIsEditModalOpen(true),
          style: "bg-gray-900 text-white hover:bg-gray-600",
        }
      : {
          text: "Invite More Guests",
          icon: <UserPlus size={18} />,
          action: () => setIsInviteModalOpen(true),
          style: "bg-white/10 text-white hover:bg-black/80",
        };

    return (
      <div className="sticky bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
        <button
          onClick={buttonProps.action}
          className={`w-full font-bold py-3 rounded-full text-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${buttonProps.style}`}
        >
          {buttonProps.icon} {buttonProps.text}
          
        </button>
      </div>
    );
  };

  return (
    <div className="bg-background text-white min-h-screen font-sans selection:bg-white selection:text-black">
      {/* Background Gradient */}
      <div className="fixed inset-0 pointer-events-none">
         <div className="absolute top-[10%] left-[20%] w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-[120px]" />
         <div className="absolute bottom-[10%] right-[20%] w-[500px] h-[500px] bg-secondary-500/10 rounded-full blur-[120px]" />
      </div>

      <main className="flex-grow relative z-10">
        <div className="container mx-auto px-6 py-24 md:py-32">
          {/* --- Page Header --- */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-widest mb-8"
            >
              <ArrowLeft size={16} /> Back
            </button>
          </motion.div>

          {/* --- Title & Actions --- */}
          <div className="flex justify-between items-end mb-12">
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400 leading-tight">
              {eventName}
            </h1>
            {isHost && (
              <div className="relative">
                {/* A simple dropdown implementation using details/summary */}
                <details className="group">
                  <summary className="list-none cursor-pointer p-3 rounded-xl hover:bg-white/10 transition-colors bg-white/5 border border-white/5">
                    <MoreVertical className="text-gray-300" size={20} />
                  </summary>
                  <div className="absolute right-0 mt-3 w-56 bg-surface/80 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-20 overflow-hidden transform scale-95 group-open:scale-100 transition-all origin-top-right">
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="flex items-center gap-3 w-full px-5 py-3 text-left text-sm text-white hover:bg-white/10 transition-colors"
                    >
                      <Pencil size={16} className="text-blue-400" /> Edit Event
                    </button>
                    <button
                      onClick={confirmCancel}
                      className="flex items-center gap-3 w-full px-5 py-3 text-left text-sm text-red-300 hover:bg-red-500/20 transition-colors"
                    >
                      <Trash2 size={16} className="text-red-400" /> Cancel Event
                    </button>
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* --- Main Content Grid --- */}
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Left Pane - Event Info */}
            <div className="lg:col-span-1 space-y-8">
               <div className="bg-surface/30 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                  <h2 className="font-bold text-xl text-white mb-6 flex items-center gap-2">
                     <span className="w-1.5 h-6 bg-secondary-500 rounded-full"></span>
                     Details
                  </h2>
                  <div className="space-y-2">
                    <DetailRow
                      icon={<CalendarDays size={20} className="text-secondary-400" />}
                      text={moment(eventDateTime).format("dddd, MMMM D, YYYY")}
                    />
                    <DetailRow
                      icon={<Clock size={20} className="text-secondary-400" />}
                      text={moment(eventDateTime).format("h:mm A")}
                    />
                    <VenueRow venue={venue} />
                  </div>

                   <div className="pt-6 mt-6 border-t border-white/10">
                     <div className="flex items-start gap-4">
                        <FileText size={20} className="text-secondary-400 flex-shrink-0 mt-1" />
                        <p className="text-gray-300 leading-relaxed text-sm">
                           {description || "No description provided."}
                        </p>
                     </div>
                  </div>
               </div>
            </div>

            {/* Right Pane - Invitee Status */}
            <div className="lg:col-span-2">
               <div className="bg-surface/20 backdrop-blur-sm rounded-3xl p-8 border border-white/5">
                  <h2 className="font-bold text-2xl text-white mb-6 flex items-center gap-2">
                     <span className="w-1.5 h-6 bg-primary-500 rounded-full"></span>
                     Invitee Status
                  </h2>
                  <div className="space-y-3">
                    {inviteeRecords.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500 bg-black/20 rounded-2xl border border-white/5">
                         <UserPlus size={48} className="mb-4 opacity-50" />
                         <p>No one has been invited yet.</p>
                      </div>
                    ) : (
                      inviteeRecords.map((invitee, index) => (
                        <details
                          key={index}
                          className="group bg-white/5 border border-white/5 rounded-xl open:bg-white/10 transition-colors"
                        >
                          <summary className="list-none flex justify-between items-center p-5 cursor-pointer">
                            <span className="font-semibold text-white">
                              {invitee.userContact || "Unknown Invitee"}
                            </span>
                            <div className="flex items-center gap-4">
                              <InviteeStatusPill status={invitee.status} />
                              <ChevronDown
                                className="text-gray-400 transition-transform duration-300 group-open:rotate-180"
                                size={18}
                              />
                            </div>
                          </summary>
                          <div className="px-5 pb-5 border-t border-white/5">
                            {Object.keys(invitee.rsvpResponses || {}).length ===
                            0 ? (
                              <p className="text-gray-500 text-sm italic py-4 flex items-center gap-2">
                                 <Clock size={14} /> Awaiting response...
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                                {Object.entries(invitee.rsvpResponses).map(
                                  ([qId, answer]) => (
                                    <div
                                      key={qId}
                                      className="bg-black/20 p-3 rounded-lg border border-white/5"
                                    >
                                      <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                                        {questionMap[qId] || "Custom Question"}
                                      </p>
                                      <p className="font-semibold text-white">
                                        {typeof answer === "boolean"
                                          ? answer
                                            ? "Yes"
                                            : "No"
                                          : answer}
                                      </p>
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </details>
                      ))
                    )}
                  </div>
               </div>
            </div>
          </div>
        </div>
        {renderStickyActionButton()}
      </main>

      <div className="container mx-auto px-6">
        <AppFooter />
      </div>
      <AnimatePresence>
        {isEditModalOpen && (
          <EditEventModal
            event={event}
            onClose={() => setIsEditModalOpen(false)}
            onEventUpdated={refetchEvent}
          />
        )}
        {isInviteModalOpen && (
          <InviteGuestsModal
            event={event}
            onClose={() => setIsInviteModalOpen(false)}
            onInvitesSent={refetchEvent}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventDetails;
